import type { FieldOverlay } from "./formFields";

/**
 * Map a Form 001 field id to a help section key.
 *
 * Help is section-level (not per-field) — many checkboxes in the same row
 * share a single explanation. The mapping is prefix-based with a small list
 * of explicit overrides for outliers (e.g. lab-row element ids that aren't
 * prefixed `p1-...`).
 *
 * The returned key looks up `formHelp.<section>.title` and
 * `formHelp.<section>.body` in the locale files.
 */

const PREFIX_RULES: Array<[RegExp, string]> = [
  [/^p[12]-r1care/, "r1Care"],
  [/^p[12]-r2care/, "r2Care"],
  [/^p[12]-vitals/, "vitals"],
  [/^p[12]-vent/, "ventilation"],
  [/^p[12]-diag/, "diagnostics"],
  [/^p[12]-lab/, "labs"],
  [/^p[12]-exam/, "exam"],
  [/^p[12]-medication/, "medications"],
  [/^p[12]-tourniquet/, "tourniquets"],
  [/^p[12]-surgery/, "surgery"],
  [/^p[12]-cpr/, "cpr"],
  [/^p[12]-prior-care/, "priorCare"],
  [/^p[12]-prior-/, "priorCare"],
  [/^p[12]-discharge/, "discharge"],
  [/^p[12]-trauma/, "trauma"],
  [/^p[12]-condition/, "conditions"],
  [/^p[12]-cbrn/, "cbrn"],
  [/^p[12]-death/, "death"],
  [/^p[12]-(r1-)?kit/, "kits"],
  [/^p[12]-medic-/, "medicSignoff"],
  [/^p[12]-(injury|burn|trauma-zone|chest-sym)/, "bodyDiagram"],
  [/^p[12]-signature/, "signature"],
  [
    /^p[12]-(name|id|dob|rank|unit|blood-type|blood-unknown|rh-factor|allergy)/,
    "patient",
  ],
  [/^p[12]-(event|arrival|facility)/, "arrival"],
  [/^p[12]-tetanus/, "tetanus"],
  [/^p[12]-(diagnosis|notes)/, "diagnosisNotes"],
  [/^p[12]-case-number/, "caseNumber"],
  [/^p[12]-escort/, "escort"],
  [/^p[12]-pupils-equal/, "pupils"],
];

/** Lab-row element ids (lab table cells, not prefixed with `p1-` / `p2-`). */
const LAB_ELEMENT_IDS = new Set([
  "hg",
  "hct",
  "rbc",
  "wbc",
  "plt",
  "na",
  "k",
  "ca",
  "cl",
  "lactate",
  "urea",
  "creatinine",
  "glucose",
  "ph",
  "pco2",
  "po2",
  "hco3",
]);

export function getHelpSection(fieldId: string): string {
  if (LAB_ELEMENT_IDS.has(fieldId)) return "labs";
  for (const [pattern, section] of PREFIX_RULES) {
    if (pattern.test(fieldId)) return section;
  }
  return "fallback";
}

export interface SectionBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Colour tiers: each section belongs to a group, each group has its own hue.
 * The form gets a coloured "group" tint behind a coloured "section" border,
 * sharing the same hue so the user can see at a glance which sections cluster
 * together.
 */
export const SECTION_TO_GROUP: Record<string, string> = {
  caseNumber: "identification",
  patient: "identification",
  arrival: "identification",
  priorCare: "prehospital",
  r1Care: "role1Care",
  tetanus: "role1Care",
  kits: "role1Care",
  cpr: "role1Care",
  vitals: "assessment",
  ventilation: "assessment",
  diagnostics: "assessment",
  labs: "assessment",
  exam: "assessment",
  pupils: "assessment",
  trauma: "injuries",
  conditions: "injuries",
  cbrn: "injuries",
  bodyDiagram: "injuries",
  medications: "treatment",
  tourniquets: "treatment",
  surgery: "treatment",
  death: "outcome",
  discharge: "outcome",
  escort: "outcome",
  medicSignoff: "signoff",
  diagnosisNotes: "signoff",
  signature: "signoff",
  r2Care: "role2Care",
  // Hardcoded-bounds-only sections (no fields map to these; they're triggered
  // by button click on a fixed region).
  triage: "identification",
  bloodType: "identification",
  r2Arrival: "identification",
  pillPack: "prehospital",
  priorCareProvider: "prehospital",
  r2Diagnostics: "assessment",
  notes3: "signoff",
  notes11: "signoff",
  notes24: "signoff",

  // Numbered subsections — primary keys used by HARDCODED_BOUNDS.
  // Page 1
  s1_1: "identification",
  s1_2: "identification",
  s1_3: "identification",
  s1_4: "identification",
  s2_1: "prehospital",
  s2_2: "prehospital",
  s2_3: "prehospital",
  s2_4: "prehospital",
  s2_5: "prehospital",
  s2_6: "prehospital",
  s3_1: "injuries",
  s3_2: "injuries",
  s3_3: "injuries",
  s3_4: "injuries",
  s3_5: "injuries",
  s4_1: "role1Care",
  s4_2: "role1Care",
  s4_3: "role1Care",
  s4_4: "role1Care",
  s4_5: "role1Care",
  s4_6: "role1Care",
  s4_7: "role1Care",
  s4_8: "role1Care",
  s4_9: "role1Care",
  s4_10: "role1Care",
  s4_11: "role1Care",
  s4_12: "role1Care",
  s5: "assessment",
  s6: "assessment",
  s7: "role1Care",
  s8: "outcome",
  s9: "assessment",
  s10: "treatment",
  s10_2: "treatment",
  s10_3: "identification",
  s11: "signoff",
  s12: "signoff",
  s13: "outcome",
  s14: "signoff",
  // Page 2
  s15: "identification",
  s16: "assessment",
  s17: "assessment",
  s18_1: "role2Care",
  s18_2: "role2Care",
  s18_3: "role2Care",
  s18_4: "role2Care",
  s18_5: "role2Care",
  s18_6: "role2Care",
  s18_7: "role2Care",
  s18_8: "role2Care",
  s18_9: "role2Care",
  s18_10: "treatment",
  s18_11: "treatment",
  s18_12: "treatment",
  s18_13: "treatment",
  s18_14: "treatment",
  s18_15: "treatment",
  s19: "assessment",
  s20: "assessment",
  s21: "role2Care",
  s22: "outcome",
  s23: "treatment",
  s23_1: "treatment",
  s23_2: "identification",
  s24: "signoff",
  s25: "signoff",
  s26: "outcome",
  s27: "signoff",
};

export interface GroupColours {
  /** Base/border colour for sections in the group. */
  base: string;
  /** Faint background tint for the group rectangle. */
  groupTint: string;
  /** Slightly stronger tint for section rectangles when hovered or active. */
  sectionActiveTint: string;
}

export const GROUP_COLOURS: Record<string, GroupColours> = {
  identification: {
    base: "#2563eb",
    groupTint: "rgba(37, 99, 235, 0.06)",
    sectionActiveTint: "rgba(37, 99, 235, 0.18)",
  },
  prehospital: {
    base: "#0891b2",
    groupTint: "rgba(8, 145, 178, 0.06)",
    sectionActiveTint: "rgba(8, 145, 178, 0.18)",
  },
  role1Care: {
    base: "#7c3aed",
    groupTint: "rgba(124, 58, 237, 0.06)",
    sectionActiveTint: "rgba(124, 58, 237, 0.18)",
  },
  assessment: {
    base: "#16a34a",
    groupTint: "rgba(22, 163, 74, 0.06)",
    sectionActiveTint: "rgba(22, 163, 74, 0.18)",
  },
  injuries: {
    base: "#dc2626",
    groupTint: "rgba(220, 38, 38, 0.06)",
    sectionActiveTint: "rgba(220, 38, 38, 0.18)",
  },
  treatment: {
    base: "#ea580c",
    groupTint: "rgba(234, 88, 12, 0.06)",
    sectionActiveTint: "rgba(234, 88, 12, 0.18)",
  },
  outcome: {
    base: "#475569",
    groupTint: "rgba(71, 85, 105, 0.07)",
    sectionActiveTint: "rgba(71, 85, 105, 0.20)",
  },
  signoff: {
    base: "#a16207",
    groupTint: "rgba(161, 98, 7, 0.06)",
    sectionActiveTint: "rgba(161, 98, 7, 0.18)",
  },
  role2Care: {
    base: "#be185d",
    groupTint: "rgba(190, 24, 93, 0.06)",
    sectionActiveTint: "rgba(190, 24, 93, 0.20)",
  },
};

export function getGroup(sectionKey: string): string | null {
  return SECTION_TO_GROUP[sectionKey] ?? null;
}

export function getGroupColours(sectionKey: string): GroupColours {
  const group = getGroup(sectionKey);
  return (group && GROUP_COLOURS[group]) || GROUP_COLOURS.identification;
}

export interface FieldHelpContent {
  /** Display title for the help popup. */
  title: string;
  /** Body text. */
  body: string;
  /** Group key for colour theming. */
  group: string;
}

/**
 * Per-field text overrides — only fill in entries here when the section-level
 * body isn't specific enough for a particular field. By default every field
 * inherits its section's body and uses its own `label` as the title.
 */
const FIELD_OVERRIDES: Record<string, { title?: string; body?: string }> = {};

/**
 * Resolve help content for a click target. The target is either a FieldOverlay
 * `id` (e.g. "p1-name") in which case the field's `label` becomes the title
 * and the section's body is used; or a section key (e.g. "vitals") in which
 * case the section's title and body are used directly.
 */
export function getFieldHelpContent(
  idOrSectionKey: string,
  fields: FieldOverlay[],
  t: (key: string) => string,
): FieldHelpContent {
  const sectionKey = getHelpSection(idOrSectionKey);
  const group = SECTION_TO_GROUP[sectionKey] ?? "identification";
  const override = FIELD_OVERRIDES[idOrSectionKey];

  const field = fields.find((f) => f.id === idOrSectionKey);
  if (field) {
    return {
      title:
        override?.title ||
        field.label ||
        t(`formHelp.${sectionKey}.title`),
      body: override?.body || t(`formHelp.${sectionKey}.body`),
      group,
    };
  }

  // Section-key path (used by hardcoded-bounds buttons for table sections)
  return {
    title: override?.title || t(`formHelp.${idOrSectionKey}.title`),
    body: override?.body || t(`formHelp.${idOrSectionKey}.body`),
    group,
  };
}

/**
 * Bounds for sections that have no FieldOverlay rectangles (or that need to
 * cover an area larger than the bounding box of their fields). The body
 * diagram is the canonical example: only one chest-symmetry field is in
 * `FORM_FIELDS`, but the visible silhouette occupies a much larger area.
 */
/**
 * Hardcoded bounds for sections that either have no FieldOverlays (the
 * time-series tables, the medication / pill-pack rows, the notes lines) or
 * whose overlay-derived bounds are too narrow to anchor a sensible button
 * (R1 / R2 care blocks, R2 examination block).
 *
 * Coordinates are approximate — measured visually from Form 001 layout. If
 * any button lands on a poor spot, just tweak the x/y/w/h here.
 */
export const HARDCODED_BOUNDS: Record<number, Record<string, SectionBounds>> = {
  1: {
    // 1.1 Patient demographics (name, sex, ID, DOB)
    s1_1: { x: 3, y: 7, w: 22, h: 6 },
    // 1.2 Rank, unit, allergy
    s1_2: { x: 27, y: 7, w: 22, h: 6 },
    // 1.3 Event time, arrival, transport, medical facility
    s1_3: { x: 51, y: 7, w: 30, h: 6 },
    // 1.4 Triage category
    s1_4: { x: 87.48, y: 8.4, w: 8, h: 2 },
    // 2.1 Pre-arrival timestamp
    s2_1: { x: 3, y: 14.5, w: 22, h: 2 },
    // 2.2 Pre-arrival provider (СД/НП/МедП/Невідомо/Не надавалась)
    s2_2: { x: 29, y: 14, w: 2, h: 1.5 },
    // 2.3 Pre-arrival vitals (RR/SpO2/Pulse/BP/AVPU/pain)
    s2_3: { x: 3, y: 17, w: 50, h: 2 },
    // 2.4 Pre-arrival treatments checklist (y≈19.25–25.55)
    s2_4: { x: 3, y: 19, w: 28, h: 7 },
    // 2.5 Pre-arrival tourniquets (limb cb + time grid, y≈20.47–26.14)
    s2_5: { x: 31, y: 20, w: 30, h: 7 },
    // 2.6 Pre-arrival medications / pill pack (rows y≈28.09–34.05)
    s2_6: { x: 3, y: 28, w: 58, h: 7 },
    // 3.1 Trauma type
    s3_1: { x: 61, y: 14, w: 2, h: 1.5 },
    // 3.2 Conditions (somatic/infectious/psychiatric/poisoning at x=61.21, y=19.25–24.07)
    s3_2: { x: 61, y: 19, w: 12, h: 7 },
    // 3.3 CBRN (Хімічне/Біологічне/Радіологічне/Ядерне at x=61.93, y=28.16–31.67)
    s3_3: { x: 61, y: 28, w: 12, h: 4 },
    // 3.4 Body diagram silhouette
    s3_4: { x: 72.57, y: 18.89, w: 25.53, h: 17.94 },
    // 3.5 Mid-form trauma notes (textarea at y=38.31)
    s3_5: { x: 3, y: 38, w: 93, h: 3 },
    // 4.1 R1 Tourniquet by limb (limb cb y=43.22–48.27, AT/VT times y=48.89)
    s4_1: { x: 3, y: 42, w: 27, h: 8 },
    // 4.2 R1 Wound care (bandage/redressing/packing/woundCare, y=50.95–52.24)
    s4_2: { x: 3, y: 50, w: 27, h: 3 },
    // 4.3 R1 Airway (sd/nfp/ofp/ngp y=41.97, ett/cricothyrotomy y=43.26)
    s4_3: { x: 30, y: 41, w: 18, h: 3 },
    // 4.4 R1 O2 / ventilation / thoracocentesis (shvl y=45.77, thoraco y=48.38–50.28)
    s4_4: { x: 30, y: 45, w: 18, h: 6 },
    // 4.5 R1 Vascular access (pvd/cvd y=41.97, io* y=43.26)
    s4_5: { x: 51, y: 41, w: 22, h: 3 },
    // 4.6 R1 Hypothermia prevention (active/passive y=47.12, detail y=47.9)
    s4_6: { x: 51, y: 47, w: 22, h: 2 },
    // 4.7 R1 Immobilisation (sub-types y=50.95, limbs y=52.24)
    s4_7: { x: 51, y: 50, w: 18, h: 3 },
    // 4.8 R1 Urinary catheter / gastric tube (y=41.97 / y=43.26)
    s4_8: { x: 76, y: 41, w: 12, h: 3 },
    // 4.9 R1 REBOA (y=41.97)
    s4_9: { x: 88, y: 41, w: 8, h: 2 },
    // 4.10 R1 Decontamination (y=45.65)
    s4_10: { x: 76, y: 45, w: 20, h: 2 },
    // 4.11 R1 Anesthesia (anesthesia y=48.14, regional y=49.31)
    s4_11: { x: 76, y: 48, w: 20, h: 3 },
    // 4.12 R1 Eye shield — match 4.11 column placement (button at far left of row, before "4.12")
    s4_12: { x: 76, y: 52, w: 22, h: 2 },
    // 5 Vitals timeline (rows y=57.20–59.94)
    s5: { x: 3, y: 56, w: 47, h: 5 },
    // 6 Ventilation timeline — anchor button to the left of the "6" badge / above section divider
    s6: { x: 50, y: 56, w: 47, h: 5 },
    // 7 CPR start/end (y=61.84–63.18)
    s7: { x: 3, y: 61, w: 25, h: 3 },
    // 8 Death — anchor button next to the "8" badge at the start of the row
    s8: { x: 25, y: 61, w: 73, h: 3 },
    // 9 Diagnostics + lab values (radio y=65.83, ecg y=67.17)
    s9: { x: 3, y: 65, w: 95, h: 4 },
    // 10 Medications administered (rows y=72.11–77.27)
    s10: { x: 3, y: 71, w: 95, h: 7 },
    // 10.2 Tetanus prophylaxis (tetanus y=79.17, cb y=78.4)
    s10_2: { x: 3, y: 78, w: 47, h: 2 },
    // 10.3 Blood type confirmation (blood y=79.17, unknown cb y=78.4)
    s10_3: { x: 50, y: 78, w: 47, h: 2 },
    // 11 Notes textarea (y=83.95) — align with "11 Нотатки" badge above the textarea
    s11: { x: 3, y: 81, w: 75, h: 8 },
    // 12 Diagnosis (R1) + kits — align with "12 Діагноз" badge above the textarea (y=89.91)
    s12: { x: 3, y: 87, w: 95, h: 5 },
    // 13 Discharge / evacuation block (y=92.66–95.54)
    s13: { x: 3, y: 92, w: 95, h: 4 },
    // 14 Medic signoff (medic-name y=97.56, signature y=97.36)
    s14: { x: 3, y: 97, w: 95, h: 2 },
  },
  2: {
    // 15 Arrival at Role 2 (time/date y=5.17)
    s15: { x: 3, y: 5, w: 80, h: 2 },
    // 16 Physical examination (y≈9.6–22.99)
    s16: { x: 3, y: 9, w: 95, h: 14 },
    // 17 R2 Diagnostics + labs (radio y=28.61, ecg y=30.71)
    s17: { x: 3, y: 28, w: 95, h: 4 },
    // Section 18 subsections — bb.y aligned to the small "18.X" superscript label
    // that sits at the top of each subsection's first row.
    // 18.1 R2 Tourniquet limbs (y=36.20–41.08)
    s18_1: { x: 3, y: 37, w: 18, h: 5 },
    // 18.2 R2 Airway — button shifted left of "18.2" label so the label stays visible
    s18_2: { x: 20, y: 35.5, w: 28, h: 3 },
    // 18.3 R2 Occlusive patch / needle decompression
    s18_3: { x: 20, y: 38, w: 28, h: 2 },
    // 18.4 R2 Vascular access
    s18_4: { x: 20, y: 41, w: 28, h: 3 },
    // 18.5 R2 Hypothermia prevention
    s18_5: { x: 20, y: 44, w: 28, h: 3 },
    // 18.6 R2 Urinary catheter / gastric tube
    s18_6: { x: 47, y: 35.5, w: 24, h: 2 },
    // 18.7 R2 Immobilisation
    s18_7: { x: 47, y: 37, w: 24, h: 3 },
    // 18.8 R2 Eye shield — button just left of "18.8" label, in its column (cb at x=85)
    s18_8: { x: 74, y: 35.5, w: 22, h: 2 },
    // 18.9 R2 Decontamination — button just left of "18.9" label (cb at x=76.32)
    s18_9: { x: 74, y: 37, w: 22, h: 2 },
    // 18.10 R2 Surgery types
    s18_10: { x: 47, y: 39.5, w: 49, h: 5 },
    // 18.11 R2 REBOA (cb at x=49.76, y=44.72)
    s18_11: { x: 47, y: 45, w: 10, h: 2 },
    // 18.12 R2 Suturing / debridement (suturing x=59.3, debridement x=72.41, y=44.72)
    s18_12: { x: 57, y: 45, w: 39, h: 2 },
    // 18.13 R2 Operative intervention description — "18.13" label sits well above textarea (y=51.28)
    s18_13: { x: 3, y: 49, w: 95, h: 5 },
    // 18.14 R2 Anesthesia type (cb y=54.02, notes textarea y=55.69)
    s18_14: { x: 3, y: 54, w: 95, h: 3 },
    // 18.15 R2 Operation start/end time (y=57.36)
    s18_15: { x: 3, y: 57, w: 95, h: 2 },
    // 19 R2 Vitals timeline (rows y=61.83–64.58)
    s19: { x: 3, y: 60, w: 47, h: 5 },
    // 20 R2 Ventilation timeline (rows y=61.83–64.58)
    s20: { x: 50, y: 60, w: 47, h: 5 },
    // 21 R2 CPR (y=66.48–67.82)
    s21: { x: 3, y: 66, w: 25, h: 3 },
    // 22 R2 Death — anchor button next to the "22" badge at the start of the row
    s22: { x: 25, y: 66, w: 73, h: 3 },
    // 23 R2 Medications administered (rows y=72.11–77.27)
    s23: { x: 3, y: 71, w: 95, h: 7 },
    // 23.1 R2 Tetanus prophylaxis (y=79.17)
    s23_1: { x: 3, y: 78, w: 47, h: 2 },
    // 23.2 R2 Blood type (y=79.17)
    s23_2: { x: 50, y: 78, w: 47, h: 2 },
    // 24 R2 Notes textarea (y=83.95) — align with "24 Нотатки" badge above the textarea
    s24: { x: 3, y: 81, w: 75, h: 8 },
    // 25 R2 Diagnosis + kits — align with "25 Діагноз" badge above the textarea (y=89.91)
    s25: { x: 3, y: 87, w: 95, h: 5 },
    // 26 R2 Discharge (y=92.66–95.54)
    s26: { x: 3, y: 92, w: 95, h: 4 },
    // 27 R2 Medic signoff (medic-name y=97.56)
    s27: { x: 3, y: 97, w: 95, h: 2 },
  },
};

/**
 * Compute the bounding rectangle of each help section on each page, by taking
 * the union of all FieldOverlay rectangles (and their labelRect, where
 * present) that map to the same section. Hardcoded bounds override or extend
 * the computed values.
 */
export function computeSectionBoundsByPage(
  fields: FieldOverlay[],
): Record<number, Record<string, SectionBounds>> {
  const acc: Record<
    number,
    Record<string, { minX: number; minY: number; maxX: number; maxY: number }>
  > = { 1: {}, 2: {} };

  for (const field of fields) {
    const section = getHelpSection(field.id);
    if (section === "fallback") continue;

    const rects: Array<{ x: number; y: number; w: number; h: number }> = [
      { x: field.x, y: field.y, w: field.w, h: field.h },
    ];
    if (field.labelRect) rects.push(field.labelRect);

    for (const r of rects) {
      const minX = r.x;
      const minY = r.y;
      const maxX = r.x + r.w;
      const maxY = r.y + r.h;
      const cur = acc[field.page][section];
      if (cur) {
        if (minX < cur.minX) cur.minX = minX;
        if (minY < cur.minY) cur.minY = minY;
        if (maxX > cur.maxX) cur.maxX = maxX;
        if (maxY > cur.maxY) cur.maxY = maxY;
      } else {
        acc[field.page][section] = { minX, minY, maxX, maxY };
      }
    }
  }

  const result: Record<number, Record<string, SectionBounds>> = { 1: {}, 2: {} };
  for (const page of [1, 2] as const) {
    for (const [section, b] of Object.entries(acc[page])) {
      result[page][section] = {
        x: b.minX,
        y: b.minY,
        w: b.maxX - b.minX,
        h: b.maxY - b.minY,
      };
    }
    for (const [section, b] of Object.entries(HARDCODED_BOUNDS[page] ?? {})) {
      result[page][section] = b;
    }
  }
  return result;
}

/**
 * Group bounds = union of the bounds of all sections that belong to the group,
 * per page. Padded slightly so the group rectangle clearly contains the
 * sections inside it.
 */
export function computeGroupBoundsByPage(
  sectionBoundsByPage: Record<number, Record<string, SectionBounds>>,
  pad = 0.4,
): Record<number, Record<string, SectionBounds>> {
  const result: Record<number, Record<string, SectionBounds>> = { 1: {}, 2: {} };
  for (const page of [1, 2] as const) {
    const acc: Record<
      string,
      { minX: number; minY: number; maxX: number; maxY: number }
    > = {};
    for (const [sectionKey, b] of Object.entries(
      sectionBoundsByPage[page] ?? {},
    )) {
      const group = getGroup(sectionKey);
      if (!group) continue;
      const minX = b.x;
      const minY = b.y;
      const maxX = b.x + b.w;
      const maxY = b.y + b.h;
      const cur = acc[group];
      if (cur) {
        if (minX < cur.minX) cur.minX = minX;
        if (minY < cur.minY) cur.minY = minY;
        if (maxX > cur.maxX) cur.maxX = maxX;
        if (maxY > cur.maxY) cur.maxY = maxY;
      } else {
        acc[group] = { minX, minY, maxX, maxY };
      }
    }
    for (const [group, b] of Object.entries(acc)) {
      result[page][group] = {
        x: Math.max(0, b.minX - pad),
        y: Math.max(0, b.minY - pad),
        w: b.maxX - b.minX + 2 * pad,
        h: b.maxY - b.minY + 2 * pad,
      };
    }
  }
  return result;
}
